import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { isNotEmpty, isPrimitiveType, Json, primitiveType } from '@se/cp.common';
import {
    fillTemplateWithData,
    getValueAtPathInObject,
    jsonToIntegrationStoreData,
    PARSABLE_PROPS,
    pathExistsInObject,
    processPostParseFunctionsParams,
} from '../../routines';
import { EOperator, FullIntegrationStoreData, IExternalIntegrationCall } from '../../tp-integrations.interface';
import { FUNCTIONS_LIST } from '../../constants';

@Component({
    template: '',
    standalone: true,
})
export abstract class AbstractCallComponent implements OnInit {
    @Input() dataStore!: FullIntegrationStoreData;
    @Input() call!: IExternalIntegrationCall;
    @Input() debug: boolean = false;

    @Output() callEnd: EventEmitter<FullIntegrationStoreData> = new EventEmitter();
    @Output() callThrowError: EventEmitter<string> = new EventEmitter();

    readonly functionsList: Record<string, Function> = FUNCTIONS_LIST;
    target: string | null = null;

    result: any;

    readonly showLoading = signal<boolean>(false);

    /**
     * mappa di comparatori per operatore
     * il valore recuperato dal datastore (value) puo essere un qualunque oggetto, array o valore primitivo
     * essendo il risultato di un'operazione interna o esterna.
     *
     * @param value - il valore da confrontare preso dal risultato dell'ultima operazione.
     * @param valueTrigger - valore di riferimento da confrontare con value
     * @returns risultato della comparazione
     */
    operatorHandlerMap: Record<EOperator, (value: primitiveType, valueTrigger: primitiveType) => boolean> = {
        [EOperator.GREATER]: (value, valueTrigger) =>
            isNotEmpty(value) && isNotEmpty(valueTrigger) && value > valueTrigger,
        [EOperator.EQUAL]: (value, valueTrigger) => value === valueTrigger,
        [EOperator.GREATER_EQUAL]: (value, valueTrigger) =>
            isNotEmpty(value) && isNotEmpty(valueTrigger) && value >= valueTrigger,
        [EOperator.LESS]: (value, valueTrigger) =>
            isNotEmpty(value) && isNotEmpty(valueTrigger) && value < valueTrigger,
        [EOperator.LESS_EQUAL]: (value, valueTrigger) =>
            isNotEmpty(value) && isNotEmpty(valueTrigger) && value <= valueTrigger,
        [EOperator.NOT_EQUAL]: (value, valueTrigger) => value !== valueTrigger,
    };

    ngOnInit(): void {
        this.target = this.call.target ?? null;
        if (this.debug) {
            console.log(`INTEGRATIONS ${this.call.type} - ${this.call.description} ngInit()`, {
                target: this.target,
                dataStore: this.dataStore,
            });
        }
    }

    async callInit() {
        this.result = await this.run();
        this.end();
    }

    protected async run(): Promise<any> {
        return null;
    }

    protected parseAllSourceWithDataStore(
        call: IExternalIntegrationCall,
        _pre: string = 'B_',
    ): Partial<IExternalIntegrationCall> {
        const parsedProps = PARSABLE_PROPS.map((prop) => {
            const propValue = call[prop];

            if (typeof propValue === 'string' && propValue.indexOf('{B_') !== -1) {
                return [prop, fillTemplateWithData(this.dataStore, call.placeholders, propValue, _pre)];
            } else if (typeof propValue === 'object' && propValue) {
                const parsedValue = JSON.parse(JSON.stringify(propValue), (key, value) => {
                    if (typeof value !== 'string' || value.indexOf('{B_') === -1) return value;

                    const splitted = value.split('_');
                    if (splitted.length === 2 && splitted[0] === '{B' && splitted[1].endsWith('}')) {
                        /*siamo sicuri che ci sia un solo {B_x} senza altre stringhe inserire direttamente il valore B_x senza altre elaborazioni
                         cosi si evita il caso in cui la stringa interpolata contenga un 'oggetto non parsabile per via degli apici esterni*/
                        const bIndex = parseInt(splitted[1].substring(0, splitted[1].length - 1));
                        const placeholderPath = call.placeholders[bIndex];
                        return this.dataStore[placeholderPath];
                    } else {
                        return fillTemplateWithData(this.dataStore, call.placeholders, value, _pre);
                    }
                });
                return [prop, parsedValue];
            }
            return [prop, call[prop]];
        });
        return Object.fromEntries(parsedProps);
    }

    protected applyPostParseProcessing(call: IExternalIntegrationCall = this.call) {
        if (!call.postParseProcessing) return call;
        Object.entries(call.postParseProcessing).forEach(([keyPath, config]) => {
            const { fnName, params } = config;
            if (!fnName || !params) return;
            if (!this.functionsList[fnName]) return;
            const fullPath = keyPath.split('.');
            if (!pathExistsInObject(call, fullPath)) return;

            const valueAtPath = getValueAtPathInObject(call, fullPath);

            const parentObj =
                fullPath.length > 1 ? getValueAtPathInObject(call, fullPath.slice(0, -1) as string[]) : call;
            const fn = this.functionsList[fnName];
            const parsedParams = processPostParseFunctionsParams(params, call.placeholders, this.dataStore);
            parentObj[fullPath[fullPath.length - 1]] = parsedParams.length
                ? fn(valueAtPath, ...parsedParams)
                : fn(valueAtPath);
        });

        return call;
    }

    protected getValueStoreDataFromPath(): Json | primitiveType {
        const answer = this.result;
        if (isPrimitiveType(answer)) return answer;
        const value: FullIntegrationStoreData = jsonToIntegrationStoreData(answer);
        const path = this.call.errorHandling?.path || '';
        return path ? value[path] : value;
    }

    protected end() {
        const output: FullIntegrationStoreData = {};
        if (this.debug) {
            console.log('INTEGRATIONS abstractCall end()', { result: this.result, target: this.target });
        }
        /*ci devono essere sia target che result altrimenti l'output e` vuoto. come accade negli iframe
         * nelle rest invece ci aspettiamo che entrambi siano settati*/
        if (this.target) output[this.target] = this.result;
        this.callEnd.emit(jsonToIntegrationStoreData(output));
    }

    protected errorValueHandler(value: Json | primitiveType): boolean {
        const { errorHandling } = this.call;
        if (value === undefined) value = null;
        if (this.debug) {
            console.log('INTEGRATIONS abstractCall erroValueHandler()', { value, errorHandling });
        }

        if (!errorHandling || errorHandling.operator === undefined) return false;
        if (!this.operatorHandlerMap[errorHandling.operator] || !isPrimitiveType(value)) return false;
        return this.operatorHandlerMap[errorHandling.operator](value, errorHandling.valueTrigger);
    }
}
